import type { SmartList, SubtaskDisplayT, Task, TaskSwipeActionT } from '@portfolio-pm/shared';
import { parseSmartAdd } from '@portfolio-pm/smart-add';
import { Menu, Plus, SlidersHorizontal } from 'lucide-react';
import {
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNow } from '../../lib/useNow.ts';
import { getDocStore } from '../../store/doc-store.ts';
import {
  DISPLAY_LIMITS,
  type DisplayDensity,
  useDisplayPreferences,
} from '../display/display-preferences.ts';
import { SmartListForm, type SmartListFormValue } from '../layout/sidebar/SmartListForm.tsx';
import { hasSearchCriteria, matchesTaskSearch } from '../search/workspace-search.ts';
import { BoardView } from './BoardView.tsx';
import { CalendarView, rescheduleTaskScheduledAt } from './CalendarView.tsx';
import { MobileAddTaskScreen, QuickAdd } from './QuickAdd.tsx';
import { TaskDetailPanel } from './TaskDetailPanel.tsx';
import { TimelineView } from './TimelineView.tsx';
import { TaskNotesWorkspace } from './detail/TaskNotesWorkspace.tsx';
import { type TaskRowDateMode, startOfDayLocal } from './task-view-dates.ts';
import {
  BUILT_IN_TASK_ARRANGEMENTS,
  DEFAULT_TASK_ARRANGEMENT,
  DEFAULT_TASK_ARRANGEMENT_ID,
  type TaskArrangementView,
  type TaskCompletionFilter,
  type TaskDisplayMode,
  type TaskWorkspaceGroup,
  type TaskWorkspaceView,
  buildNestedTaskGroups,
  buildTaskWorkspaceCounts,
  buildTaskWorkspaceGroups,
  compareTasks,
  filterTasksForTaskView,
  getTaskDisplayModeLabel,
  getTaskWorkspaceLabel,
  getTaskWorkspaceListKey,
  matchesTaskCompletionFilter,
} from './task-workspace-model.ts';
import { BulkEditDialog, SelectionPresetPopover } from './workspace/BulkEditDialog.tsx';
import { MobileTaskSelector } from './workspace/MobileTaskSelector.tsx';
import {
  TaskArrangementManagerDialog,
  TaskArrangementPopover,
} from './workspace/TaskArrangementControls.tsx';
import {
  CompleteChildrenDialog,
  DeleteRecurringTaskDialog,
  MobilePostponeDelayDialog,
  TaskListPane,
} from './workspace/TaskListPane.tsx';
import {
  type BulkEditDraft,
  MOBILE_TASK_VIEW_QUERY,
  PRIORITY_OPTIONS,
  type PostponeDelayUnit,
  type PostponeOptionId,
  SELECTION_PRESETS,
  type SelectionPresetId,
} from './workspace/options.ts';
import {
  buildBulkEditPatch,
  buildPostponeDelayPatch,
  buildPostponePatch,
  buildShortcutPostponePatch,
  buildSmartPostponePatch,
  clampNumber,
  getTaskSummaryQuery,
  isTaskDueOnOffset,
  isTaskOverdueForSelection,
  matchesMobileTaskView,
} from './workspace/task-actions.ts';
import {
  DateToolbarMenu,
  IconToolbarButton,
  PostponeToolbarMenu,
  ProjectToolbarMenu,
  TagsToolbarMenu,
  ToolbarGlyph,
  ToolbarIconMenu,
} from './workspace/toolbar.tsx';

export interface TaskWorkspaceProps {
  view: TaskWorkspaceView;
  displayMode: TaskDisplayMode;
  onChangeView(view: TaskWorkspaceView): void;
  onChangeDisplayMode(mode: TaskDisplayMode): void;
  onOpenSettings?(): void;
  searchQuery?: string;
  onClearSearch?(): void;
}

export function TaskWorkspace({
  view,
  displayMode,
  onChangeView,
  onChangeDisplayMode,
  onOpenSettings,
  searchQuery = '',
  onClearSearch,
}: TaskWorkspaceProps): JSX.Element {
  const store = getDocStore();
  const { preferences: displayPreferences, updatePreferences: updateDisplayPreferences } =
    useDisplayPreferences();
  const projects = store.useProjects();
  const allProjects = store.useAllProjects();
  const activeProject = store.useActiveProject();
  const snapshot = store.useFocusSnapshot();
  const uiPreferences = store.useUiPreferences();

  const [activeTaskId, setActiveTaskId] = useState<string | undefined>(undefined);
  const [notesWorkspaceTaskId, setNotesWorkspaceTaskId] = useState<string | undefined>();
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(() => new Set());
  const [completionFilter, setCompletionFilter] = useState<TaskCompletionFilter>('incomplete');
  const [pendingCompleteRequest, setPendingCompleteRequest] = useState<
    { taskIds: string[] } | undefined
  >();
  const [pendingDeleteRequest, setPendingDeleteRequest] = useState<
    { taskIds: string[] } | undefined
  >();
  const [pendingSwipePostponeTaskId, setPendingSwipePostponeTaskId] = useState<
    string | undefined
  >();
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [mobileAddOpen, setMobileAddOpen] = useState(false);
  const [inspectedSubtaskId, setInspectedSubtaskId] = useState<string | undefined>(undefined);
  const [mobileSelectorOpen, setMobileSelectorOpen] = useState(false);
  const [mobileListControlsOpen, setMobileListControlsOpen] = useState(false);
  const [viewSelectorOpen, setViewSelectorOpen] = useState<false | 'mobile' | 'desktop'>(false);
  const [selectionMenuOpen, setSelectionMenuOpen] = useState(false);
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState<
    | false
    | 'row-date'
    | 'display'
    | 'subtasks'
    | 'completion'
    | 'postpone'
    | 'priority'
    | 'workflow'
    | 'project'
    | 'due'
    | 'scheduled'
    | 'tags'
    | 'more'
  >(false);
  const [viewManagerOpen, setViewManagerOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [editingArrangementId, setEditingArrangementId] = useState<string | undefined>();
  const [showBulkSelection, setShowBulkSelection] = useState(() => !matchesMobileTaskView());
  const [rowDateMode, setRowDateMode] = useState<TaskRowDateMode>('due');
  const detailTouchStartX = useRef<number | undefined>(undefined);
  const listTouchStartRef = useRef<
    | {
        x: number;
        y: number;
        valid: boolean;
      }
    | undefined
  >(undefined);
  const listRef = useRef<HTMLDivElement>(null);
  const titleFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const selectionMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileArrangementButtonRef = useRef<HTMLButtonElement>(null);
  const desktopArrangementButtonRef = useRef<HTMLButtonElement>(null);
  const now = useNow();

  // Latest-value refs so row callbacks can stay identity-stable across renders
  // (memoised TaskRow props) while still reading current view state.
  const activeTaskIdRef = useRef(activeTaskId);
  activeTaskIdRef.current = activeTaskId;
  const snapshotTasksRef = useRef(snapshot.tasks);
  snapshotTasksRef.current = snapshot.tasks;

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(MOBILE_TASK_VIEW_QUERY);
    const update = (): void => setShowBulkSelection(!query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const projectsById = useMemo(
    () => new Map(allProjects.map((project) => [project.id, project])),
    [allProjects],
  );
  const workspaceTags = store.useTags();
  const smartListsById = useMemo(
    () => new Map(snapshot.smartLists.map((smartList) => [smartList.id, smartList])),
    [snapshot.smartLists],
  );
  const customArrangements = snapshot.taskArrangements;
  const allArrangements = useMemo<TaskArrangementView[]>(
    () => [...BUILT_IN_TASK_ARRANGEMENTS, ...customArrangements],
    [customArrangements],
  );
  const arrangementById = useMemo(
    () => new Map(allArrangements.map((arrangement) => [arrangement.id, arrangement])),
    [allArrangements],
  );
  const listKey = getTaskWorkspaceListKey(view);
  const selectedArrangement = snapshot.selectedTaskArrangements.find(
    (selected) => selected.listKey === listKey,
  );
  const selectedArrangementId = selectedArrangement?.arrangementId ?? DEFAULT_TASK_ARRANGEMENT_ID;
  const subtaskDisplay = selectedArrangement?.subtaskDisplay ?? 'flat';
  const activeArrangement =
    arrangementById.get(selectedArrangementId) ??
    arrangementById.get(DEFAULT_TASK_ARRANGEMENT_ID) ??
    DEFAULT_TASK_ARRANGEMENT;
  const searchActive = hasSearchCriteria(searchQuery);
  const effectiveDisplayMode: TaskDisplayMode = searchActive ? 'list' : displayMode;

  const searchedViewTasks = useMemo(() => {
    const activeProjectIds = new Set(projects.map((project) => project.id));
    return snapshot.tasks
      .filter((task) => matchesTaskSearch(task, searchQuery, now))
      .filter((task) => task.trashedAt === undefined && activeProjectIds.has(task.projectId));
  }, [now, projects, searchQuery, snapshot.tasks]);

  const filteredTasks = useMemo(
    () => filterTasksForTaskView(snapshot.tasks, view, now, snapshot.smartLists, allProjects),
    [allProjects, now, snapshot.smartLists, snapshot.tasks, view],
  );

  const viewTasks = useMemo(
    () =>
      filteredTasks.filter((task) => {
        if (view.kind === 'system' && view.id === 'trash') return task.trashedAt !== undefined;
        if (view.kind !== 'smart') return task.trashedAt === undefined;
        const smartList = snapshot.smartLists.find(
          (candidate) => candidate.id === view.smartListId,
        );
        return smartList?.showTrashed ? true : task.trashedAt === undefined;
      }),
    [filteredTasks, snapshot.smartLists, view],
  );

  const smartListShowsCompleted =
    view.kind === 'smart'
      ? (snapshot.smartLists.find((smartList) => smartList.id === view.smartListId)
          ?.showCompleted ?? true)
      : false;
  const completionDefault =
    view.kind === 'system' && view.id === 'trash'
      ? 'both'
      : smartListShowsCompleted
        ? 'both'
        : 'incomplete';

  useEffect(() => {
    setCompletionFilter(completionDefault);
  }, [completionDefault]);

  const listGroups = useMemo(
    () =>
      buildTaskWorkspaceGroups({
        tasks: snapshot.tasks,
        projects: allProjects,
        smartLists: snapshot.smartLists,
        view,
        completionFilter,
        now,
        groupBy: activeArrangement.groupBy,
        groupDirection: activeArrangement.groupDirection,
        sort: activeArrangement.sort,
        subtaskDisplay,
      }),
    [
      activeArrangement,
      completionFilter,
      now,
      allProjects,
      snapshot.smartLists,
      snapshot.tasks,
      subtaskDisplay,
      view,
    ],
  );

  const searchedTasks = useMemo(
    () =>
      searchedViewTasks
        .filter((task) => matchesTaskCompletionFilter(task, completionFilter))
        .sort(compareTasks),
    [completionFilter, searchedViewTasks],
  );

  const searchGroups = useMemo<TaskWorkspaceGroup[]>(() => {
    if (subtaskDisplay === 'nested') {
      return buildNestedTaskGroups({
        tasks: searchedTasks,
        projects: allProjects,
        groupBy: 'project',
        groupDirection: 'asc',
        sort: activeArrangement.sort,
      }).map((group) => ({
        ...group,
        id: `search:${group.id}`,
        label: `Search · ${group.label}`,
      }));
    }

    const grouped = new Map<string, Task[]>();
    for (const task of searchedTasks) {
      const bucket = grouped.get(task.projectId) ?? [];
      bucket.push(task);
      grouped.set(task.projectId, bucket);
    }
    return [...grouped.entries()].map(([projectId, tasks]) => ({
      id: `search:${projectId}`,
      label: `Search · ${projectsById.get(projectId)?.name ?? 'Project'}`,
      tasks,
    }));
  }, [activeArrangement.sort, allProjects, projectsById, searchedTasks, subtaskDisplay]);

  const visibleListGroups = searchActive ? searchGroups : listGroups;

  const baseDisplayTasks = useMemo(
    () =>
      (searchActive
        ? searchedTasks
        : viewTasks.filter((task) => matchesTaskCompletionFilter(task, completionFilter))
      ).sort(compareTasks),
    [completionFilter, searchActive, searchedTasks, viewTasks],
  );

  const displayTasks = useMemo(() => {
    switch (effectiveDisplayMode) {
      case 'calendar':
        return baseDisplayTasks.filter(
          (task) => task.scheduledAt !== undefined || task.dueAt !== undefined,
        );
      case 'timeline':
        return view.kind === 'project'
          ? baseDisplayTasks.filter(
              (task) =>
                task.projectId === view.projectId &&
                (task.scheduledAt !== undefined || task.dueAt !== undefined),
            )
          : [];
      default:
        return baseDisplayTasks;
    }
  }, [baseDisplayTasks, effectiveDisplayMode, view]);

  const selectableTasks = useMemo(
    () =>
      effectiveDisplayMode === 'list'
        ? visibleListGroups.flatMap((group) => group.tasks)
        : displayTasks,
    [displayTasks, effectiveDisplayMode, visibleListGroups],
  );

  useEffect(() => {
    if (activeTaskId !== undefined && selectableTasks.some((task) => task.id === activeTaskId)) {
      return;
    }
    setActiveTaskId(undefined);
  }, [selectableTasks, activeTaskId]);

  useEffect(() => {
    const selectableIds = new Set(selectableTasks.map((task) => task.id));
    setSelectedTaskIds((current) => {
      const next = new Set([...current].filter((taskId) => selectableIds.has(taskId)));
      return next.size === current.size ? current : next;
    });
  }, [selectableTasks]);

  useEffect(() => {
    const selectedFromUrl = new URLSearchParams(window.location.search).get('task');
    if (!selectedFromUrl) return;
    if (!selectableTasks.some((task) => task.id === selectedFromUrl)) return;

    setActiveTaskId(selectedFromUrl);
    if (effectiveDisplayMode === 'list') listRef.current?.focus();

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('task');
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  }, [effectiveDisplayMode, selectableTasks]);

  useEffect(() => {
    if (effectiveDisplayMode !== 'timeline') return;
    if (view.kind === 'project') return;
    setActiveTaskId(undefined);
  }, [effectiveDisplayMode, view]);

  const activeTask = activeTaskId
    ? snapshot.tasks.find((task) => task.id === activeTaskId)
    : undefined;
  const notesWorkspaceTask = notesWorkspaceTaskId
    ? snapshot.tasks.find((task) => task.id === notesWorkspaceTaskId)
    : undefined;
  const inspectedSubtask = inspectedSubtaskId
    ? snapshot.tasks.find((task) => task.id === inspectedSubtaskId)
    : undefined;
  const pendingSwipePostponeTask = pendingSwipePostponeTaskId
    ? snapshot.tasks.find((task) => task.id === pendingSwipePostponeTaskId)
    : undefined;

  const selectedTasks = useMemo(
    () => snapshot.tasks.filter((task) => selectedTaskIds.has(task.id)),
    [selectedTaskIds, snapshot.tasks],
  );
  const actionTasks = selectedTasks.length > 0 ? selectedTasks : activeTask ? [activeTask] : [];
  const selectedToolbarTags = useMemo(
    () =>
      [...new Set(selectedTasks.flatMap((task) => task.tags))].sort((left, right) =>
        left.localeCompare(right, undefined, { sensitivity: 'base' }),
      ),
    [selectedTasks],
  );

  const blockedTaskIds = useMemo(
    () =>
      new Set(snapshot.tasks.filter((task) => store.isTaskBlocked(task.id)).map((task) => task.id)),
    [snapshot.tasks, store],
  );

  const counts = useMemo(
    () => buildTaskWorkspaceCounts(snapshot.tasks, allProjects, snapshot.smartLists, now),
    [allProjects, now, snapshot.smartLists, snapshot.tasks],
  );

  const objectivesForProject = useMemo(
    () =>
      view.kind === 'project'
        ? snapshot.objectives.filter((objective) => objective.projectId === view.projectId)
        : [],
    [snapshot.objectives, view],
  );

  useEffect(() => {
    if (!inspectedSubtask) {
      setInspectedSubtaskId(undefined);
      return;
    }
    if (!activeTask || inspectedSubtask.parentTaskId !== activeTask.id) {
      setInspectedSubtaskId(undefined);
    }
  }, [activeTask, inspectedSubtask]);

  const moveSelection = (delta: 1 | -1): void => {
    if (selectableTasks.length === 0) return;
    const index = selectableTasks.findIndex((task) => task.id === activeTaskId);
    const nextIndex =
      index === -1 ? 0 : Math.max(0, Math.min(selectableTasks.length - 1, index + delta));
    setActiveTaskId(selectableTasks[nextIndex]?.id);
  };

  const requestToggleTaskComplete = useCallback(
    (taskId: string): void => {
      const tasks = snapshotTasksRef.current;
      const task = tasks.find((candidate) => candidate.id === taskId);
      if (!task) return;
      const openChildren = tasks.filter(
        (candidate) => candidate.parentTaskId === task.id && candidate.completedAt === undefined,
      );
      if (task.completedAt === undefined && openChildren.length > 0) {
        setPendingCompleteRequest({ taskIds: [task.id] });
        return;
      }
      store.toggleTaskComplete(task.id);
    },
    [store],
  );

  const applyTaskSwipeAction = useCallback(
    (task: Task, action: TaskSwipeActionT): void => {
      if (action === 'complete') {
        if (task.completedAt === undefined) requestToggleTaskComplete(task.id);
        return;
      }
      setPendingSwipePostponeTaskId(task.id);
    },
    [requestToggleTaskComplete],
  );

  const applySwipePostponeDelay = (
    task: Task,
    delay: { amount: number; unit: PostponeDelayUnit },
  ): void => {
    store.updateTask(task.id, buildPostponeDelayPatch(task, delay, Date.now()));
    setPendingSwipePostponeTaskId(undefined);
  };

  const inspectTask = useCallback((taskId: string): void => {
    setActiveTaskId(taskId);
    setInspectedSubtaskId(undefined);
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 1023px)').matches
    ) {
      setMobileDetailOpen(true);
    }
  }, []);

  const selectTaskRow = useCallback(
    (taskId: string): void => {
      if (activeTaskIdRef.current === taskId) {
        setActiveTaskId(undefined);
        setInspectedSubtaskId(undefined);
        setSelectedTaskIds(new Set());
        return;
      }

      if (matchesMobileTaskView()) {
        inspectTask(taskId);
        return;
      }

      setActiveTaskId(taskId);
      setInspectedSubtaskId(undefined);
      setSelectedTaskIds(new Set([taskId]));
      if (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 1023px)').matches
      ) {
        setMobileDetailOpen(true);
      }
    },
    [inspectTask],
  );

  const toggleTaskSelection = useCallback((taskId: string): void => {
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }, []);

  const reorderTasks = useCallback(
    (orderedTaskIds: string[]): void => {
      store.reorderTasks(orderedTaskIds);
    },
    [store],
  );

  const inspectChildTask = (taskId: string): void => {
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 1023px)').matches
    ) {
      inspectTask(taskId);
      return;
    }
    setInspectedSubtaskId(taskId);
  };

  const clearTaskSelection = (): void => setSelectedTaskIds(new Set());

  const applySelectionPreset = useCallback(
    (presetId: SelectionPresetId): void => {
      setSelectionMenuOpen(false);
      if (presetId === 'none') {
        setSelectedTaskIds(new Set());
        return;
      }

      const nextTasks = selectableTasks.filter((task) => {
        switch (presetId) {
          case 'all':
            return true;
          case 'overdue':
            return isTaskOverdueForSelection(task, now);
          case 'due-today':
            return isTaskDueOnOffset(task, now, 0);
          case 'due-tomorrow':
            return isTaskDueOnOffset(task, now, 1);
        }
      });
      setSelectedTaskIds(new Set(nextTasks.map((task) => task.id)));
    },
    [now, selectableTasks],
  );

  useEffect(() => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<string>).detail;
      if (detail === 'a') applySelectionPreset('all');
      if (detail === 'n') applySelectionPreset('none');
      if (detail === 'o') applySelectionPreset('overdue');
      if (detail === 't') applySelectionPreset('due-today');
    };
    window.addEventListener('portfolio-shortcut-select', handler);
    return () => window.removeEventListener('portfolio-shortcut-select', handler);
  }, [applySelectionPreset]);

  const postponeSelectedTasks = (optionId: PostponeOptionId): void => {
    for (const task of actionTasks) {
      store.updateTask(task.id, buildPostponePatch(task, optionId, now));
    }
  };

  const shortcutPostponeSelectedTasks = (): void => {
    for (const task of actionTasks) {
      store.updateTask(task.id, buildShortcutPostponePatch(task, now));
    }
  };

  const postponeSelectedTasksFromText = (value: string): boolean => {
    let applied = false;
    for (const task of actionTasks) {
      const patch = buildSmartPostponePatch(task, value, now);
      if (Object.keys(patch).length === 0) continue;
      store.updateTask(task.id, patch);
      applied = true;
    }
    return applied;
  };

  const completeToolbarTasks = (): void => {
    const targets = selectedTasks;
    if (targets.length === 0) return;
    const shouldReopen = targets.every((task) => task.completedAt !== undefined);
    const tasksToToggle = targets.filter(
      (task) => shouldReopen === (task.completedAt !== undefined),
    );
    if (!shouldReopen) {
      const tasksWithOpenChildren = tasksToToggle.filter((task) =>
        snapshot.tasks.some(
          (candidate) => candidate.parentTaskId === task.id && candidate.completedAt === undefined,
        ),
      );
      if (tasksWithOpenChildren.length > 0) {
        setPendingCompleteRequest({ taskIds: tasksToToggle.map((task) => task.id) });
        return;
      }
    }
    for (const task of tasksToToggle) {
      requestToggleTaskComplete(task.id);
    }
    clearTaskSelection();
  };

  const duplicateToolbarTasks = (): void => {
    const duplicatedIds: string[] = [];
    for (const task of selectedTasks) {
      const duplicate = store.duplicateTask(task.id);
      if (duplicate) duplicatedIds.push(duplicate.id);
    }
    if (duplicatedIds.length === 0) return;
    setSelectedTaskIds(new Set(duplicatedIds));
    setActiveTaskId(duplicatedIds[0]);
    setToolbarMenuOpen(false);
  };

  const requestDeleteTasks = (tasks: Task[]): void => {
    if (tasks.length === 0) return;
    if (tasks.some((task) => task.recurrence !== undefined)) {
      setPendingDeleteRequest({ taskIds: tasks.map((task) => task.id) });
      setToolbarMenuOpen(false);
      return;
    }
    for (const task of tasks) store.removeTask(task.id);
    clearTaskSelection();
    setActiveTaskId(undefined);
    setInspectedSubtaskId(undefined);
    setToolbarMenuOpen(false);
  };

  const deleteToolbarTasks = (): void => requestDeleteTasks(actionTasks);

  const applyPendingRecurringDelete = (deleteSeries: boolean): void => {
    if (!pendingDeleteRequest) return;
    const tasks = snapshot.tasks.filter((task) => pendingDeleteRequest.taskIds.includes(task.id));
    for (const task of tasks) {
      store.removeTask(task.id, {
        deleteSeries,
        generateNext: !deleteSeries && task.recurrence !== undefined,
      });
    }
    clearTaskSelection();
    setActiveTaskId(undefined);
    setInspectedSubtaskId(undefined);
    setToolbarMenuOpen(false);
    setPendingDeleteRequest(undefined);
  };

  const restoreToolbarTasks = (): void => {
    for (const task of actionTasks) store.restoreTask(task.id);
    clearTaskSelection();
    setToolbarMenuOpen(false);
  };

  const patchToolbarTasks = (patch: Parameters<typeof store.updateTask>[1]): void => {
    for (const task of actionTasks) store.updateTask(task.id, patch);
  };

  const toggleToolbarFavorite = (): void => {
    const favorite = !selectedTasks.every((task) => task.favorite === true);
    patchToolbarTasks({ favorite });
    setToolbarMenuOpen(false);
  };

  const applyBulkEdit = (draft: BulkEditDraft): void => {
    const patch = buildBulkEditPatch(draft);
    if (Object.keys(patch).length === 0) {
      setBulkEditOpen(false);
      return;
    }
    patchToolbarTasks(patch);
    setBulkEditOpen(false);
  };

  const promptPatchText = (
    label: string,
    buildPatch: (value: string) => Parameters<typeof store.updateTask>[1],
  ): void => {
    if (actionTasks.length === 0) return;
    const value = window.prompt(label);
    if (value === null) return;
    patchToolbarTasks(buildPatch(value));
  };

  const promptPatchDate = (label: string, field: 'dueAt' | 'scheduledAt'): void => {
    promptPatchText(label, (value) => {
      const trimmed = value.trim();
      if (!trimmed) {
        return field === 'dueAt'
          ? { dueAt: undefined, dueAtHasTime: undefined }
          : { scheduledAt: undefined, scheduledAtHasTime: undefined };
      }
      const parsed = Date.parse(trimmed);
      if (Number.isNaN(parsed)) return {};
      const hasTime = /\d:\d|\dam|\dpm/i.test(trimmed);
      return field === 'dueAt'
        ? { dueAt: hasTime ? parsed : startOfDayLocal(parsed), dueAtHasTime: hasTime }
        : { scheduledAt: hasTime ? parsed : startOfDayLocal(parsed), scheduledAtHasTime: hasTime };
    });
  };

  const promptPatchReminder = (): void => {
    promptPatchText('Reminder', (value) => {
      const trimmed = value.trim();
      if (!trimmed) return { reminderAt: undefined };
      const parsed = Date.parse(trimmed);
      return Number.isNaN(parsed) ? {} : { reminderAt: parsed };
    });
  };

  const promptPatchRecurrence = (): void => {
    promptPatchText('Repeat rule', (value) => {
      const trimmed = value.trim();
      if (!trimmed) return { recurrence: undefined };
      const parsed = parseSmartAdd(`Task *${trimmed}`, { now: new Date(now) });
      if (parsed.error === undefined && parsed.recurrence !== undefined) {
        return {
          recurrence: {
            rrule: parsed.recurrence.rrule,
            fromCompletion: parsed.recurrence.fromCompletion,
          },
        };
      }
      return { recurrence: { rrule: trimmed, fromCompletion: false } };
    });
  };

  const focusActiveTaskNotes = (): void => {
    if (!activeTaskId) return;
    document.getElementById(`${activeTaskId}-activity-note`)?.focus();
  };

  const selectMobileTaskView = (nextView: TaskWorkspaceView): void => {
    onChangeView(nextView);
    setMobileSelectorOpen(false);
    setViewSelectorOpen(false);
    setMobileDetailOpen(false);
  };

  const handleListTouchStart = (event: TouchEvent<HTMLElement>): void => {
    if (effectiveDisplayMode !== 'list') {
      listTouchStartRef.current = undefined;
      return;
    }

    const target = event.target;
    const startedOnTask =
      target instanceof Element && target.closest('[data-testid="task-row"]') !== null;
    const touch = event.touches[0];
    listTouchStartRef.current =
      touch === undefined
        ? undefined
        : {
            x: touch.clientX,
            y: touch.clientY,
            valid: !startedOnTask,
          };
  };

  const handleListTouchEnd = (event: TouchEvent<HTMLElement>): void => {
    const start = listTouchStartRef.current;
    listTouchStartRef.current = undefined;
    const end = event.changedTouches[0];
    if (!start?.valid || end === undefined) return;

    const deltaX = end.clientX - start.x;
    const deltaY = end.clientY - start.y;
    if (Math.abs(deltaX) < 70 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

    if (deltaX > 0) setMobileSelectorOpen(true);
    if (deltaX < 0) setMobileSelectorOpen(false);
  };

  const handleDetailTouchStart = (event: TouchEvent<HTMLDivElement>): void => {
    detailTouchStartX.current = event.touches[0]?.clientX;
  };

  const handleDetailTouchEnd = (event: TouchEvent<HTMLDivElement>): void => {
    const startX = detailTouchStartX.current;
    detailTouchStartX.current = undefined;
    const endX = event.changedTouches[0]?.clientX;
    if (startX === undefined || endX === undefined) return;
    if (endX - startX > 60) closeMobileDetail();
  };

  const closeMobileDetail = (): void => {
    setMobileDetailOpen(false);
    setActiveTaskId(undefined);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    switch (event.key) {
      case 'j':
      case 'ArrowDown':
        event.preventDefault();
        moveSelection(1);
        return;
      case 'k':
      case 'ArrowUp':
        event.preventDefault();
        moveSelection(-1);
        return;
      case 'x':
      case 'c':
      case ' ':
        event.preventDefault();
        if (selectedTasks.length > 0) {
          completeToolbarTasks();
          return;
        }
        if (!activeTaskId) return;
        requestToggleTaskComplete(activeTaskId);
        return;
      case 'h':
        event.preventDefault();
        setCompletionFilter((current) => (current === 'completed' ? 'incomplete' : 'completed'));
        return;
      case 'i':
        if (!activeTaskId) return;
        event.preventDefault();
        toggleTaskSelection(activeTaskId);
        return;
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        deleteToolbarTasks();
        return;
      case 'z':
        event.preventDefault();
        store.undoLastChange();
        return;
      case 'e':
      case 'Enter':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          completeToolbarTasks();
          return;
        }
        if (!activeTaskId) return;
        event.preventDefault();
        titleFieldRef.current?.focus();
        titleFieldRef.current?.select();
        return;
      case '1':
      case '2':
      case '3':
      case '4':
        event.preventDefault();
        patchToolbarTasks({
          priority: (event.key === '4' ? 0 : Number(event.key)) as Task['priority'],
        });
        return;
      case 'd':
        event.preventDefault();
        promptPatchDate('Due date', 'dueAt');
        return;
      case 'p':
        event.preventDefault();
        shortcutPostponeSelectedTasks();
        return;
      case 's':
      case 'w':
        event.preventDefault();
        promptPatchDate('Scheduled date', 'scheduledAt');
        return;
      case 'f':
        event.preventDefault();
        promptPatchText('Tags', (value) => ({
          tags: value
            .split(',')
            .map((tag) => tag.trim().replace(/^#/, ''))
            .filter(Boolean),
        }));
        return;
      case 'u':
        event.preventDefault();
        if (event.shiftKey && activeTask?.url) {
          window.open(activeTask.url, '_blank', 'noopener,noreferrer');
          return;
        }
        promptPatchText('URL', (value) => ({ url: value.trim() || undefined }));
        return;
      case 'o':
      case 'v':
        event.preventDefault();
        promptPatchText('Owner', (value) => ({ owner: value.trim() || undefined }));
        return;
      case 'r':
        event.preventDefault();
        promptPatchRecurrence();
        return;
      case 'l':
        event.preventDefault();
        promptPatchText('Location', (value) => ({ location: value.trim() || undefined }));
        return;
      case 'b':
        event.preventDefault();
        promptPatchReminder();
        return;
      case 'y':
        event.preventDefault();
        focusActiveTaskNotes();
        return;
      case 'm':
        event.preventDefault();
        promptPatchText('Estimate minutes', (value) => ({
          estimateMinutes: Number(value) || undefined,
        }));
        return;
      case '.':
        if (selectedTasks.length === 0) return;
        event.preventDefault();
        setToolbarMenuOpen('more');
        return;
    }
  };

  const viewLabel = getTaskWorkspaceLabel(view, projectsById, smartListsById);
  const viewDescription = searchActive
    ? searchQuery.trim()
    : view.kind === 'smart'
      ? (smartListsById.get(view.smartListId)?.query ?? '')
      : getTaskSummaryQuery(view, viewLabel);
  const activeSmartList =
    view.kind === 'smart'
      ? snapshot.smartLists.find((smartList) => smartList.id === view.smartListId)
      : undefined;
  const toolbarTaskCount = selectedTasks.length;
  const toolbarTargets = selectedTasks;
  const toolbarTargetsComplete =
    toolbarTargets.length > 0 && toolbarTargets.every((task) => task.completedAt !== undefined);
  const toolbarTargetsFavorited =
    toolbarTargets.length > 0 && toolbarTargets.every((task) => task.favorite === true);
  const completeToolbarLabel = toolbarTargetsComplete
    ? `Reopen ${selectedTasks.length} selected task${selectedTasks.length === 1 ? '' : 's'}`
    : `Complete ${selectedTasks.length} selected task${selectedTasks.length === 1 ? '' : 's'}`;
  const hasToolbarTarget = selectedTasks.length > 0;
  const displayModeOptions = (['list', 'board', 'calendar', 'timeline'] as const).filter(
    (mode) => mode !== 'timeline' || view.kind === 'project' || effectiveDisplayMode === 'timeline',
  );
  const selectArrangement = (arrangementId: string): void => {
    store.selectTaskArrangement(listKey, arrangementId);
    setViewSelectorOpen(false);
    setMobileSelectorOpen(false);
  };

  const openNewArrangement = (): void => {
    setEditingArrangementId(undefined);
    setViewSelectorOpen(false);
    setMobileSelectorOpen(false);
    setViewManagerOpen(true);
  };

  const openManageArrangements = (): void => {
    setEditingArrangementId(customArrangements[0]?.id);
    setViewSelectorOpen(false);
    setMobileSelectorOpen(false);
    setViewManagerOpen(true);
  };

  const startDetailResize = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    if (window.matchMedia('(max-width: 1023px)').matches) return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = displayPreferences.taskDetailWidth;
    const handleMove = (moveEvent: PointerEvent): void => {
      const nextWidth = clampNumber(
        startWidth - (moveEvent.clientX - startX),
        DISPLAY_LIMITS.taskDetailWidth.min,
        DISPLAY_LIMITS.taskDetailWidth.max,
      );
      updateDisplayPreferences({ taskDetailWidth: nextWidth });
    };
    const handleUp = (): void => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp, { once: true });
  };

  return (
    <div className="task-workspace-grid relative h-full min-h-0 overflow-hidden md:grid">
      <MobileTaskSelector
        open={mobileSelectorOpen}
        view={view}
        projects={projects}
        smartLists={snapshot.smartLists}
        favoriteShortcuts={uiPreferences.favoriteShortcuts}
        counts={counts}
        onSelect={selectMobileTaskView}
        {...(onOpenSettings
          ? {
              onOpenSettings: () => {
                setMobileSelectorOpen(false);
                onOpenSettings();
              },
            }
          : {})}
      />
      <MobileAddTaskScreen
        open={mobileAddOpen}
        projectName={activeProject.name}
        onClose={() => setMobileAddOpen(false)}
      />
      <section
        data-testid="task-list-pane"
        onTouchStart={handleListTouchStart}
        onTouchEnd={handleListTouchEnd}
        className={`flex h-full min-h-0 flex-col overflow-hidden border-r border-[color:var(--app-border)] bg-white transition-transform duration-200 ease-out md:h-auto lg:border-r-0 ${
          mobileSelectorOpen && effectiveDisplayMode === 'list' ? 'mobile-task-list-shift' : ''
        } ${mobileDetailOpen ? 'max-lg:hidden max-md:flex' : ''}`}
      >
        {effectiveDisplayMode === 'list' && (
          <div
            data-testid="mobile-task-list-header"
            className="mobile-task-list-header mobile-standalone-safe-top relative border-b border-[color:var(--app-border)] px-4 pb-3 md:hidden"
          >
            <div className="grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-center gap-3">
              <button
                type="button"
                aria-label="Open task views"
                data-testid="mobile-task-menu-button"
                onClick={() => {
                  setViewSelectorOpen(false);
                  setMobileSelectorOpen((open) => !open);
                }}
                className="ui-button ui-button-subtle h-10 w-10 px-0"
              >
                <Menu aria-hidden="true" size={18} />
              </button>
              <div className="min-w-0">
                <button
                  type="button"
                  data-testid="mobile-task-list-controls-button"
                  aria-label={`Open ${viewLabel} list controls`}
                  aria-expanded={mobileListControlsOpen}
                  onClick={() => {
                    setMobileSelectorOpen(false);
                    setViewSelectorOpen(false);
                    setMobileListControlsOpen(true);
                  }}
                  className="block max-w-full truncate text-left text-lg font-semibold tracking-tight text-[color:var(--app-text)]"
                >
                  {viewLabel}
                </button>
                <p className="text-xs text-[color:var(--app-text-muted)]">
                  {selectableTasks.length} visible
                </p>
              </div>
              <button
                ref={mobileArrangementButtonRef}
                type="button"
                data-testid="mobile-task-arrangement-button"
                aria-label="Choose task view"
                onClick={() => {
                  setMobileSelectorOpen(false);
                  setViewSelectorOpen((open) => (open === 'mobile' ? false : 'mobile'));
                }}
                className="ui-button ui-button-subtle h-10 w-10 px-0"
              >
                <SlidersHorizontal aria-hidden="true" size={16} />
              </button>
              {viewSelectorOpen === 'mobile' && (
                <TaskArrangementPopover
                  triggerRef={mobileArrangementButtonRef}
                  scope="mobile"
                  onClose={() => setViewSelectorOpen(false)}
                  activeArrangementId={activeArrangement.id}
                  builtInArrangements={BUILT_IN_TASK_ARRANGEMENTS}
                  customArrangements={customArrangements}
                  onSelect={selectArrangement}
                  onCreate={openNewArrangement}
                  onManage={openManageArrangements}
                />
              )}
            </div>
          </div>
        )}

        <div className="toolbar-strip max-md:hidden">
          {searchActive && (
            <div className="flex min-w-max items-center gap-2 border-r border-[color:var(--app-border)] pr-2">
              <span className="ui-chip ui-chip-info">
                {searchedTasks.length} search result{searchedTasks.length === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                onClick={onClearSearch}
                className="ui-button ui-button-subtle h-9 px-3 text-xs"
              >
                Clear
              </button>
            </div>
          )}
          <div className="relative">
            <button
              ref={selectionMenuButtonRef}
              type="button"
              data-testid="task-selection-menu-button"
              aria-label="Select visible tasks"
              title="Select visible tasks"
              aria-expanded={selectionMenuOpen}
              onClick={() => setSelectionMenuOpen((open) => !open)}
              className="ui-button toolbar-icon-button"
            >
              <ToolbarGlyph name="select" />
            </button>
            {selectionMenuOpen && (
              <SelectionPresetPopover
                triggerRef={selectionMenuButtonRef}
                presets={SELECTION_PRESETS}
                visibleCount={selectableTasks.length}
                onClose={() => setSelectionMenuOpen(false)}
                onSelect={applySelectionPreset}
              />
            )}
          </div>
          <ToolbarIconMenu
            data-testid="task-completion-filter"
            label="Show completed tasks"
            open={toolbarMenuOpen === 'completion'}
            options={[
              { id: 'incomplete', label: 'Incomplete' },
              { id: 'completed', label: 'Completed' },
              { id: 'both', label: 'Both' },
            ]}
            activeId={completionFilter}
            onToggle={() =>
              setToolbarMenuOpen((open) => (open === 'completion' ? false : 'completion'))
            }
            onClose={() => setToolbarMenuOpen(false)}
            onSelect={(optionId) => {
              setCompletionFilter(optionId as TaskCompletionFilter);
              setToolbarMenuOpen(false);
            }}
          >
            {completionFilter === 'incomplete' ? (
              <ToolbarGlyph
                name="completion-open"
                data-testid="task-completion-filter-icon-incomplete"
              />
            ) : completionFilter === 'completed' ? (
              <ToolbarGlyph
                name="completion-done"
                data-testid="task-completion-filter-icon-completed"
              />
            ) : (
              <ToolbarGlyph name="completion-both" data-testid="task-completion-filter-icon-both" />
            )}
          </ToolbarIconMenu>
          {hasToolbarTarget && (
            <div
              data-testid="task-selection-toolbar"
              className="flex shrink-0 items-center gap-0 border-l border-[color:var(--app-border)] pl-0.5"
              aria-label="Selected task actions"
            >
              <div className="toolbar-button-group">
                <IconToolbarButton label={completeToolbarLabel} onClick={completeToolbarTasks}>
                  {toolbarTargetsComplete ? (
                    <ToolbarGlyph name="restore" />
                  ) : (
                    <ToolbarGlyph name="complete" />
                  )}
                </IconToolbarButton>
              </div>
              <div className="toolbar-button-group">
                <PostponeToolbarMenu
                  data-testid="task-toolbar-postpone"
                  label="Postpone selected tasks"
                  open={toolbarMenuOpen === 'postpone'}
                  onToggle={() =>
                    setToolbarMenuOpen((open) => (open === 'postpone' ? false : 'postpone'))
                  }
                  onClose={() => setToolbarMenuOpen(false)}
                  onQuickSelect={(optionId) => {
                    postponeSelectedTasks(optionId as PostponeOptionId);
                    setToolbarMenuOpen(false);
                  }}
                  onSmartSubmit={(value) => {
                    const applied = postponeSelectedTasksFromText(value);
                    if (applied) setToolbarMenuOpen(false);
                    return applied;
                  }}
                >
                  <ToolbarGlyph name="postpone" />
                </PostponeToolbarMenu>
                <DateToolbarMenu
                  data-testid="task-toolbar-due"
                  label="Set selected due date"
                  field="dueAt"
                  now={now}
                  open={toolbarMenuOpen === 'due'}
                  onToggle={() => setToolbarMenuOpen((open) => (open === 'due' ? false : 'due'))}
                  onClose={() => setToolbarMenuOpen(false)}
                  onPatch={(patch) => {
                    patchToolbarTasks(patch);
                    setToolbarMenuOpen(false);
                  }}
                >
                  <ToolbarGlyph name="due" />
                </DateToolbarMenu>
                <DateToolbarMenu
                  data-testid="task-toolbar-scheduled"
                  label="Set selected scheduled date"
                  field="scheduledAt"
                  now={now}
                  open={toolbarMenuOpen === 'scheduled'}
                  onToggle={() =>
                    setToolbarMenuOpen((open) => (open === 'scheduled' ? false : 'scheduled'))
                  }
                  onClose={() => setToolbarMenuOpen(false)}
                  onPatch={(patch) => {
                    patchToolbarTasks(patch);
                    setToolbarMenuOpen(false);
                  }}
                >
                  <ToolbarGlyph name="scheduled" />
                </DateToolbarMenu>
              </div>
              <div className="toolbar-button-group">
                <ToolbarIconMenu
                  data-testid="task-toolbar-priority"
                  label="Set selected priority"
                  open={toolbarMenuOpen === 'priority'}
                  options={PRIORITY_OPTIONS.map((option) => ({
                    id: String(option.id),
                    label: option.label,
                  }))}
                  onToggle={() =>
                    setToolbarMenuOpen((open) => (open === 'priority' ? false : 'priority'))
                  }
                  onClose={() => setToolbarMenuOpen(false)}
                  onSelect={(optionId) => {
                    patchToolbarTasks({ priority: Number(optionId) as Task['priority'] });
                    setToolbarMenuOpen(false);
                  }}
                >
                  <ToolbarGlyph name="priority" />
                </ToolbarIconMenu>
                <ToolbarIconMenu
                  data-testid="task-toolbar-workflow"
                  label="Set selected workflow"
                  open={toolbarMenuOpen === 'workflow'}
                  options={snapshot.workflowColumns.map((column) => ({
                    id: column.id,
                    label: column.name,
                  }))}
                  onToggle={() =>
                    setToolbarMenuOpen((open) => (open === 'workflow' ? false : 'workflow'))
                  }
                  onClose={() => setToolbarMenuOpen(false)}
                  onSelect={(optionId) => {
                    patchToolbarTasks({ workflowState: optionId as Task['workflowState'] });
                    setToolbarMenuOpen(false);
                  }}
                >
                  <ToolbarGlyph name="workflow" />
                </ToolbarIconMenu>
                <ProjectToolbarMenu
                  data-testid="task-toolbar-project"
                  label="Move selected project"
                  open={toolbarMenuOpen === 'project'}
                  projects={projects}
                  onToggle={() =>
                    setToolbarMenuOpen((open) => (open === 'project' ? false : 'project'))
                  }
                  onClose={() => setToolbarMenuOpen(false)}
                  onSelect={(projectId) => {
                    patchToolbarTasks({ projectId });
                    setToolbarMenuOpen(false);
                  }}
                >
                  <ToolbarGlyph name="project" />
                </ProjectToolbarMenu>
              </div>
              <div className="toolbar-button-group">
                <TagsToolbarMenu
                  data-testid="task-toolbar-tags"
                  label="Set selected tags"
                  open={toolbarMenuOpen === 'tags'}
                  existingTags={workspaceTags}
                  initialTags={selectedToolbarTags}
                  tagColors={uiPreferences.tagColors}
                  onToggle={() => setToolbarMenuOpen((open) => (open === 'tags' ? false : 'tags'))}
                  onClose={() => setToolbarMenuOpen(false)}
                  onApply={(tags) => {
                    patchToolbarTasks({ tags });
                    setToolbarMenuOpen(false);
                  }}
                >
                  <ToolbarGlyph name="tags" />
                </TagsToolbarMenu>
                <ToolbarIconMenu
                  data-testid="task-toolbar-more"
                  label="More selected task actions"
                  align="right"
                  open={toolbarMenuOpen === 'more'}
                  options={[
                    ...(view.kind === 'system' && view.id === 'trash'
                      ? [{ id: 'restore', label: `Restore ${toolbarTaskCount} selected` }]
                      : []),
                    {
                      id: 'favorite',
                      label: toolbarTargetsFavorited
                        ? `Remove favourite from ${toolbarTaskCount} selected`
                        : `Favourite ${toolbarTaskCount} selected`,
                    },
                    { id: 'duplicate', label: `Duplicate ${toolbarTaskCount} selected` },
                    { id: 'bulk-edit', label: 'Bulk edit selected' },
                    { id: 'delete', label: `Delete ${toolbarTaskCount} selected` },
                    { id: 'clear', label: 'Clear selection' },
                  ]}
                  onToggle={() => setToolbarMenuOpen((open) => (open === 'more' ? false : 'more'))}
                  onClose={() => setToolbarMenuOpen(false)}
                  onSelect={(optionId) => {
                    if (optionId === 'restore') restoreToolbarTasks();
                    if (optionId === 'favorite') toggleToolbarFavorite();
                    if (optionId === 'duplicate') duplicateToolbarTasks();
                    if (optionId === 'bulk-edit') {
                      setBulkEditOpen(true);
                      setToolbarMenuOpen(false);
                    }
                    if (optionId === 'delete') deleteToolbarTasks();
                    if (optionId === 'clear') {
                      clearTaskSelection();
                      setToolbarMenuOpen(false);
                    }
                  }}
                >
                  <ToolbarGlyph name="more" />
                </ToolbarIconMenu>
              </div>
            </div>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-0">
            <ToolbarIconMenu
              data-testid="task-row-date-mode"
              label="Row date mode"
              open={toolbarMenuOpen === 'row-date'}
              options={[
                { id: 'due', label: 'Due' },
                { id: 'scheduled', label: 'Scheduled' },
              ]}
              activeId={rowDateMode}
              onToggle={() =>
                setToolbarMenuOpen((open) => (open === 'row-date' ? false : 'row-date'))
              }
              onClose={() => setToolbarMenuOpen(false)}
              onSelect={(optionId) => {
                setRowDateMode(optionId as TaskRowDateMode);
                setToolbarMenuOpen(false);
              }}
            >
              <ToolbarGlyph name="row-date" />
            </ToolbarIconMenu>
            <div className="relative">
              <button
                ref={desktopArrangementButtonRef}
                type="button"
                data-testid="task-arrangement-button"
                aria-label={`Choose task view: ${activeArrangement.name}`}
                title={`Choose task view: ${activeArrangement.name}`}
                aria-expanded={viewSelectorOpen === 'desktop'}
                onClick={() =>
                  setViewSelectorOpen((open) => (open === 'desktop' ? false : 'desktop'))
                }
                className="ui-button toolbar-icon-button"
              >
                <ToolbarGlyph name="arrangement" />
              </button>
              {viewSelectorOpen === 'desktop' && (
                <TaskArrangementPopover
                  triggerRef={desktopArrangementButtonRef}
                  scope="desktop"
                  onClose={() => setViewSelectorOpen(false)}
                  activeArrangementId={activeArrangement.id}
                  builtInArrangements={BUILT_IN_TASK_ARRANGEMENTS}
                  customArrangements={customArrangements}
                  onSelect={selectArrangement}
                  onCreate={openNewArrangement}
                  onManage={openManageArrangements}
                />
              )}
            </div>
            {effectiveDisplayMode === 'list' && (
              <ToolbarIconMenu
                data-testid="task-subtask-display"
                label="Subtask display"
                open={toolbarMenuOpen === 'subtasks'}
                align="right"
                options={[
                  { id: 'flat', label: 'Flat, like other tasks' },
                  { id: 'nested', label: 'Nested under parent' },
                ]}
                activeId={subtaskDisplay}
                onToggle={() =>
                  setToolbarMenuOpen((open) => (open === 'subtasks' ? false : 'subtasks'))
                }
                onClose={() => setToolbarMenuOpen(false)}
                onSelect={(optionId) => {
                  store.setTaskSubtaskDisplay(listKey, optionId as SubtaskDisplayT);
                  setToolbarMenuOpen(false);
                }}
              >
                <ToolbarGlyph name="subtasks" />
              </ToolbarIconMenu>
            )}
            <ToolbarIconMenu
              data-testid="task-display-mode"
              label="Task display mode"
              open={toolbarMenuOpen === 'display'}
              align="right"
              options={displayModeOptions.map((mode) => ({
                id: mode,
                label: getTaskDisplayModeLabel(mode),
                disabled: mode === 'timeline' && view.kind !== 'project',
              }))}
              activeId={effectiveDisplayMode}
              onToggle={() =>
                setToolbarMenuOpen((open) => (open === 'display' ? false : 'display'))
              }
              onClose={() => setToolbarMenuOpen(false)}
              onSelect={(optionId) => {
                onChangeDisplayMode(optionId as TaskDisplayMode);
                setToolbarMenuOpen(false);
              }}
            >
              <ToolbarGlyph name="display" />
            </ToolbarIconMenu>
          </div>
        </div>

        <div className="pane-divider-b px-4 py-4 max-md:hidden">
          <QuickAdd projectName={activeProject.name} />
        </div>

        {effectiveDisplayMode === 'list' ? (
          <TaskListPane
            groups={visibleListGroups}
            listRef={listRef}
            activeTaskId={activeTaskId}
            selectedTaskIds={selectedTaskIds}
            priorityColors={uiPreferences.priorityColors}
            tagColors={uiPreferences.tagColors}
            showBulkSelection={showBulkSelection}
            rowDateMode={rowDateMode}
            swipeLeftToRight={uiPreferences.taskSwipeLeftToRight}
            swipeRightToLeft={uiPreferences.taskSwipeRightToLeft}
            manualOrdering={!searchActive && activeArrangement.sort[0]?.key === 'manual'}
            nestedSubtasks={subtaskDisplay === 'nested'}
            onKeyDown={handleKeyDown}
            onSelectTask={selectTaskRow}
            onToggleTaskSelection={toggleTaskSelection}
            onTaskSwipeAction={applyTaskSwipeAction}
            onReorderTasks={reorderTasks}
          />
        ) : effectiveDisplayMode === 'board' ? (
          <BoardView
            tasks={baseDisplayTasks}
            workflowColumns={snapshot.workflowColumns}
            projectsById={projectsById}
            blockedTaskIds={blockedTaskIds}
            selectedTaskId={activeTaskId}
            showCompleted={completionFilter !== 'incomplete'}
            onSelectTask={inspectTask}
            onToggleTask={requestToggleTaskComplete}
            onMoveTask={(taskId, workflowState) => {
              store.updateTask(taskId, { workflowState });
              inspectTask(taskId);
            }}
          />
        ) : effectiveDisplayMode === 'calendar' ? (
          <CalendarView
            tasks={baseDisplayTasks}
            selectedTaskId={activeTaskId}
            onSelectTask={inspectTask}
            onRescheduleTask={(taskId, targetDay) => {
              const task = snapshot.tasks.find((candidate) => candidate.id === taskId);
              if (!task) return;
              store.updateTask(taskId, {
                scheduledAt: rescheduleTaskScheduledAt(task, targetDay),
                scheduledAtHasTime:
                  task.scheduledAt !== undefined ? (task.scheduledAtHasTime ?? true) : false,
              });
            }}
          />
        ) : view.kind === 'project' ? (
          <TimelineView
            project={projectsById.get(view.projectId) ?? activeProject}
            objectives={objectivesForProject}
            tasks={baseDisplayTasks.filter((task) => task.projectId === view.projectId)}
            selectedTaskId={activeTaskId}
            onSelectTask={inspectTask}
          />
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-center p-4">
            <div className="surface-empty max-w-lg text-center">
              Timeline is available only for project views because it uses project objective dates
              and task spans. Switch to a project in the sidebar to open the six-week timeline.
            </div>
          </div>
        )}
        <button
          type="button"
          data-testid="mobile-add-task-fab"
          aria-label="Add task"
          onClick={() => setMobileAddOpen(true)}
          className="mobile-add-task-fab"
        >
          <Plus aria-hidden="true" size={22.4} strokeWidth={3.2} />
        </button>
      </section>

      {mobileListControlsOpen && (
        <MobileTaskListControls
          viewLabel={viewLabel}
          completionFilter={completionFilter}
          density={displayPreferences.density}
          smartList={activeSmartList}
          onChangeCompletionFilter={setCompletionFilter}
          onChangeDensity={(density) => updateDisplayPreferences({ density })}
          onEditSmartList={(patch) => {
            if (activeSmartList) store.updateSmartList(activeSmartList.id, patch);
            setMobileListControlsOpen(false);
          }}
          onShowStatistics={() => {
            setMobileListControlsOpen(false);
            setActiveTaskId(undefined);
            setMobileDetailOpen(true);
          }}
          onClose={() => setMobileListControlsOpen(false)}
        />
      )}

      <button
        type="button"
        aria-label="Resize task detail pane"
        data-testid="task-detail-resize-handle"
        onPointerDown={startDetailResize}
        className="task-detail-resize-handle hidden lg:block"
      />

      <div
        data-testid="mobile-task-detail-screen"
        data-open={mobileDetailOpen || undefined}
        onTouchStart={handleDetailTouchStart}
        onTouchEnd={handleDetailTouchEnd}
        className={`task-detail-stage flex h-full min-h-0 flex-col bg-white max-md:absolute max-md:inset-0 max-md:z-20 max-md:transition-transform max-md:duration-200 max-md:ease-out ${
          mobileDetailOpen
            ? 'max-md:translate-x-0 md:max-lg:flex'
            : 'max-md:translate-x-full md:max-lg:hidden'
        } ${inspectedSubtask ? 'task-detail-stage-open' : ''} lg:flex`}
      >
        <div className="mobile-standalone-safe-top shrink-0 border-b border-[color:var(--app-border)] bg-[color:var(--app-mobile-bar-bg)] px-4 pb-3 backdrop-blur md:mb-3 md:pt-3 lg:hidden">
          <button
            type="button"
            data-testid="mobile-task-detail-back"
            onClick={closeMobileDetail}
            className="ui-button ui-button-subtle"
          >
            Back to tasks
          </button>
        </div>
        <div
          data-testid="task-detail-stage"
          data-subtask-open={inspectedSubtask ? 'true' : undefined}
          className="task-detail-stage-track min-h-0 flex-1 overflow-hidden"
        >
          <div className="task-detail-stage-pane h-full min-h-0">
            <TaskDetailPanel
              projects={projects}
              workflowColumns={snapshot.workflowColumns}
              selectedTask={activeTask}
              summaryTasks={displayTasks}
              summaryCompletedCount={
                (searchActive ? searchedViewTasks : viewTasks).filter(
                  (task) => task.completedAt !== undefined,
                ).length
              }
              allTasks={snapshot.tasks}
              taskActivities={snapshot.taskActivities}
              projectsById={projectsById}
              titleFieldRef={titleFieldRef}
              currentView={view}
              viewTitle={searchActive ? 'Search results' : viewLabel}
              viewDescription={viewDescription}
              onChangeView={onChangeView}
              onSelectChildTask={inspectChildTask}
              onOpenNotesWorkspace={setNotesWorkspaceTaskId}
              onDeleteTask={(taskId) => {
                const task = snapshot.tasks.find((candidate) => candidate.id === taskId);
                if (task) requestDeleteTasks([task]);
              }}
            />
          </div>
          {inspectedSubtask && (
            <div
              data-testid="subtask-detail-pane"
              className="task-detail-stage-pane h-full min-h-0 border-l border-[color:var(--app-border)]"
            >
              <TaskDetailPanel
                projects={projects}
                workflowColumns={snapshot.workflowColumns}
                selectedTask={inspectedSubtask}
                summaryTasks={displayTasks}
                summaryCompletedCount={
                  (searchActive ? searchedViewTasks : viewTasks).filter(
                    (task) => task.completedAt !== undefined,
                  ).length
                }
                allTasks={snapshot.tasks}
                taskActivities={snapshot.taskActivities}
                projectsById={projectsById}
                titleFieldRef={titleFieldRef}
                currentView={view}
                viewTitle={searchActive ? 'Search results' : viewLabel}
                viewDescription={viewDescription}
                onChangeView={onChangeView}
                onSelectChildTask={inspectChildTask}
                onOpenNotesWorkspace={setNotesWorkspaceTaskId}
                onDeleteTask={(taskId) => {
                  const task = snapshot.tasks.find((candidate) => candidate.id === taskId);
                  if (task) requestDeleteTasks([task]);
                }}
                onClose={() => setInspectedSubtaskId(undefined)}
              />
            </div>
          )}
        </div>
      </div>
      {notesWorkspaceTask && (
        <TaskNotesWorkspace
          task={notesWorkspaceTask}
          onClose={() => setNotesWorkspaceTaskId(undefined)}
        />
      )}
      {viewManagerOpen && (
        <TaskArrangementManagerDialog
          arrangements={customArrangements}
          editingArrangementId={editingArrangementId}
          onEditingArrangementChange={setEditingArrangementId}
          onCreate={(input) => {
            const created = store.createTaskArrangement(input);
            if (created) {
              setEditingArrangementId(created.id);
              store.selectTaskArrangement(listKey, created.id);
            }
          }}
          onUpdate={(id, patch) => store.updateTaskArrangement(id, patch)}
          onDelete={(id) => {
            store.removeTaskArrangement(id);
            if (editingArrangementId === id) {
              const next = customArrangements.find((arrangement) => arrangement.id !== id);
              setEditingArrangementId(next?.id);
            }
          }}
          onClose={() => setViewManagerOpen(false)}
        />
      )}
      {bulkEditOpen && (
        <BulkEditDialog
          selectedCount={selectedTasks.length}
          onApply={applyBulkEdit}
          onClose={() => setBulkEditOpen(false)}
        />
      )}
      {pendingCompleteRequest && (
        <CompleteChildrenDialog
          taskCount={pendingCompleteRequest.taskIds.length}
          childrenCount={
            snapshot.tasks.filter(
              (task) =>
                pendingCompleteRequest.taskIds.includes(task.parentTaskId ?? '') &&
                task.completedAt === undefined,
            ).length
          }
          onSelectedOnly={() => {
            for (const taskId of pendingCompleteRequest.taskIds) {
              store.completeTaskWithChildren(taskId, false);
            }
            clearTaskSelection();
            setPendingCompleteRequest(undefined);
          }}
          onWithChildren={() => {
            for (const taskId of pendingCompleteRequest.taskIds) {
              store.completeTaskWithChildren(taskId, true);
            }
            clearTaskSelection();
            setPendingCompleteRequest(undefined);
          }}
          onCancel={() => setPendingCompleteRequest(undefined)}
        />
      )}
      {pendingDeleteRequest && (
        <DeleteRecurringTaskDialog
          taskCount={pendingDeleteRequest.taskIds.length}
          recurringCount={
            snapshot.tasks.filter(
              (task) =>
                pendingDeleteRequest.taskIds.includes(task.id) && task.recurrence !== undefined,
            ).length
          }
          onInstances={() => applyPendingRecurringDelete(false)}
          onSeries={() => applyPendingRecurringDelete(true)}
          onCancel={() => setPendingDeleteRequest(undefined)}
        />
      )}
      {pendingSwipePostponeTask && (
        <MobilePostponeDelayDialog
          taskTitle={pendingSwipePostponeTask.title}
          onQuickSelect={(delay) => applySwipePostponeDelay(pendingSwipePostponeTask, delay)}
          onCustomSubmit={(delay) => applySwipePostponeDelay(pendingSwipePostponeTask, delay)}
          onCancel={() => setPendingSwipePostponeTaskId(undefined)}
        />
      )}
    </div>
  );
}

function MobileTaskListControls({
  viewLabel,
  completionFilter,
  density,
  smartList,
  onChangeCompletionFilter,
  onChangeDensity,
  onEditSmartList,
  onShowStatistics,
  onClose,
}: {
  viewLabel: string;
  completionFilter: TaskCompletionFilter;
  density: DisplayDensity;
  smartList: SmartList | undefined;
  onChangeCompletionFilter(filter: TaskCompletionFilter): void;
  onChangeDensity(density: DisplayDensity): void;
  onEditSmartList(patch: SmartListFormValue): void;
  onShowStatistics(): void;
  onClose(): void;
}): JSX.Element {
  const [editingSmartList, setEditingSmartList] = useState(false);

  return (
    <div
      data-testid="mobile-task-list-controls"
      className="fixed inset-0 z-40 flex items-end md:hidden"
    >
      <button
        type="button"
        aria-label="Close list controls"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[color:var(--app-modal-overlay)]"
      />
      <dialog
        open
        aria-label={`${viewLabel} list controls`}
        className="relative m-0 max-h-[85vh] w-full overflow-y-auto rounded-t-xl border border-b-0 border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-[color:var(--app-text)]">{viewLabel}</h2>
          <button
            type="button"
            onClick={onClose}
            className="ui-button ui-button-subtle h-8 px-3 text-xs"
          >
            Done
          </button>
        </div>

        <label className="block space-y-1.5">
          <span className="ui-label">Completed tasks</span>
          <select
            data-testid="mobile-task-completion-filter"
            value={completionFilter}
            onChange={(event) =>
              onChangeCompletionFilter(event.target.value as TaskCompletionFilter)
            }
            className="ui-select w-full"
          >
            <option value="incomplete">Hide completed</option>
            <option value="both">Show completed</option>
            <option value="completed">Completed only</option>
          </select>
        </label>

        <label className="mt-4 block space-y-1.5">
          <span className="ui-label">List density</span>
          <select
            data-testid="mobile-task-density-select"
            value={density}
            onChange={(event) => onChangeDensity(event.target.value as DisplayDensity)}
            className="ui-select w-full"
          >
            <option value="compact">Compact</option>
            <option value="standard">Standard</option>
            <option value="comfortable">Comfortable</option>
          </select>
        </label>

        <button
          type="button"
          data-testid="mobile-task-statistics-button"
          onClick={onShowStatistics}
          className="ui-button mt-4 h-10 w-full justify-start px-3"
        >
          View statistics
        </button>

        {smartList && (
          <div className="mt-4 border-t border-[color:var(--app-border)] pt-4">
            {editingSmartList ? (
              <SmartListForm
                initialName={smartList.name}
                initialQuery={smartList.query}
                initialRuleTree={smartList.ruleTree}
                initialGroupBy={smartList.groupBy}
                initialGroupDirection={smartList.groupDirection}
                initialSort={smartList.sort}
                initialShowCompleted={smartList.showCompleted}
                initialShowTrashed={smartList.showTrashed}
                onCommit={onEditSmartList}
                onCancel={() => setEditingSmartList(false)}
              />
            ) : (
              <button
                type="button"
                data-testid="mobile-edit-smart-list-button"
                onClick={() => setEditingSmartList(true)}
                className="ui-button h-10 w-full justify-start px-3"
              >
                Edit saved view
              </button>
            )}
          </div>
        )}
      </dialog>
    </div>
  );
}
